import { useRef, useState } from "react";

import { Link } from "react-router-dom";

import {
  Dropdown,
  type DropdownProps,
  type StrictDropdownItemProps,
} from "semantic-ui-react";
import { styled } from "styled-components";

const StyledAccessibleDropdown = styled(Dropdown)`
  .header {
    margin: 0 !important;
    pointer-events: none !important;
  }
  > .text {
    font-weight: normal !important;
  }
`;

type AccessibleDropdownOption = StrictDropdownItemProps & {
  value: NonNullable<StrictDropdownItemProps["value"]>;
} & { key?: string | number; as?: "a"; href?: string; target?: string };

// Has to use "options" to enable aria attributes on dropdown items
type AccessibleDropdownProps = Omit<DropdownProps, "children" | "options"> & {
  options: (
    | (Omit<AccessibleDropdownOption, "as"> & {
        as?: AccessibleDropdownOption["as"] | typeof Link;
        to?: string;
        onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
      })
    | null
    | undefined
  )[];
};

const AccessibleDropdown = (props: AccessibleDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(props.open ?? false);
  const [internalSelection, setInternalSelection] = useState<
    AccessibleDropdownProps["options"][number]["value"] | null
  >(props.value ?? props.defaultValue ?? null);

  const options = (props.options || [])
    .filter(Boolean)
    .map((option) => ({
      key: option.key || option.value,
      value: option.value || option.key,
      ...option,
    }));
  const isOpen = props.open !== undefined ? props.open : internalOpen;

  return (
    <StyledAccessibleDropdown
      ref={dropdownRef}
      tabIndex={props.trigger ? -1 : 0}
      openOnFocus={false}
      inline
      multiple={false}
      icon={null}
      {...props}
      onKeyDown={(e) => {
        if (props.clearable && (e.key === "Backspace" || e.key === "Delete")) {
          e.preventDefault();
          setInternalSelection(null);
          props.onChange?.(e, { ...props, value: null });
        }

        if (isOpen && internalSelection) {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            const option = options.find(
              (opt) => opt.value === internalSelection,
            );
            if (option?.onClick) {
              option.onClick(e as unknown as React.MouseEvent<HTMLDivElement>, {
                ...props,
                value: option.value,
              });
            }
            if (option?.href) {
              window.open(
                option.href,
                option.target || "_self",
                "noopener noreferrer",
              );
            }
            setInternalSelection(null);
          }
        }
        props.onKeyDown?.(e);
      }}
      onChange={(e, data) => {
        setInternalSelection(
          data.value as AccessibleDropdownProps["options"][number]["value"],
        );
        props.onChange?.(e, data);
      }}
      options={options}
      onOpen={(e, data) => {
        // Only triggers if modal is uncontrolled (no open prop)
        setInternalOpen(true);
        props.onOpen?.(e, data);
      }}
      onClose={(e, data) => {
        // Only triggers if modal is uncontrolled (no open prop)
        setInternalOpen(false);
        props.onClose?.(e, data);
      }}
    />
  );
};

export { AccessibleDropdown };
